import { siteConfig } from "@acme/config";
import { createFileRoute } from "@tanstack/react-router";
import { InnerCta } from "~/components/inside/inner-cta";
import { PageIntro, PageLabel } from "~/components/inside/page-intro";
import { PageScene } from "~/components/inside/page-scene";
import { PlainList } from "~/components/inside/plain-list";
import { QuietLink } from "~/components/inside/quiet-link";
import { ReadLayout } from "~/components/inside/read-layout";
import { SideIndex } from "~/components/inside/side-index";
import { usePageEngagement } from "~/hooks/use-page-engagement";
import { getLogoCuts, getPalette, getTypeScale } from "~/lib/brand-palette";
import { publicCacheHeaders } from "~/lib/cache-headers";
import { seo } from "~/lib/seo";
import * as m from "~/paraglide/messages.js";

export const Route = createFileRoute("/(public)/brand")({
  component: BrandPage,
  head: () => {
    const seoData = seo({
      description: m.meta_brand_description(),
      keywords:
        "Lindevo brand, logo, brand guidelines, design system, colour palette, typography, Manrope",
      path: "/brand",
      title: m.meta_brand_title(),
    });
    return { links: seoData.links, meta: seoData.meta };
  },
  headers: publicCacheHeaders,
});

/** The sizes a favicon and an app icon actually get rendered at. */
const ICON_SIZES = [16, 24, 32, 48] as const;

/**
 * The identity and the system behind it, on the record.
 *
 * Every colour here is painted with its own custom property and only *labelled*
 * with a hex, so a swatch cannot show one value and claim another. The labels
 * come from `lib/brand-palette.ts`, which `lint:tokens` checks against
 * `tokens.css` on every run — the page is not allowed to describe a system the
 * app is no longer running.
 */
function BrandPage() {
  usePageEngagement({ pagePath: "/brand" });

  const cuts = getLogoCuts();
  const palette = getPalette();
  const scale = getTypeScale();

  const sections = [
    { href: "#mark", label: m.brand_mark_title() },
    { href: "#using", label: m.brand_using_title() },
    { href: "#colour", label: m.brand_colour_title() },
    { href: "#type", label: m.brand_type_title() },
    { href: "#shape", label: m.brand_shape_title() },
    { href: "#access", label: m.brand_access_title() },
  ];

  return (
    <>
      <PageScene variant="heading">
        <div>
          <PageLabel>{m.brand_label()}</PageLabel>
          <h1>{m.brand_title()}</h1>
          <PageIntro>{m.brand_intro()}</PageIntro>
        </div>

        <aside className="brand-aside">
          <h2>{m.brand_aside_title()}</h2>
          <p>{m.brand_aside_body()}</p>
          <QuietLink href={`mailto:${siteConfig.contacts.mail}`}>
            {m.brand_aside_cta()}
          </QuietLink>
        </aside>
      </PageScene>

      <ReadLayout
        index={<SideIndex items={sections} title={m.brand_index_title()} />}
      >
        <section className="brand-section" id="mark">
          <h2>{m.brand_mark_title()}</h2>
          <p>{m.brand_mark_body()}</p>

          <div className="mark-stage">
            <img
              alt={siteConfig.name}
              height={128}
              src="/images/logo-dark.svg"
              width={480}
            />
          </div>

          <div className="cut-grid">
            {cuts.map((cut) => (
              <article className={`cut is-${cut.ground}`} key={cut.file}>
                <div className="cut-sample">
                  <img
                    alt=""
                    className={cut.width === cut.height ? "is-symbol" : ""}
                    height={cut.height}
                    src={cut.file}
                    width={cut.width}
                  />
                </div>
                <h3>{cut.title}</h3>
                <p>{cut.use}</p>
                <a className="quiet-link" download href={cut.file}>
                  {m.brand_download()} <span aria-hidden="true">↓</span>
                </a>
              </article>
            ))}
          </div>
        </section>

        <section className="brand-section" id="using">
          <h2>{m.brand_using_title()}</h2>
          <p>{m.brand_using_body()}</p>

          <h3>{m.brand_sizes_title()}</h3>
          <ul className="size-row">
            {ICON_SIZES.map((size) => (
              <li key={size}>
                <img alt="" height={size} src="/images/icon.svg" width={size} />
                <span>{size} px</span>
              </li>
            ))}
          </ul>
          <p className="small-note">{m.brand_sizes_note()}</p>

          <h3>{m.brand_clearspace_title()}</h3>
          <div className="clearspace">
            <img alt="" height={64} src="/images/icon.svg" width={64} />
          </div>
          <p className="small-note">{m.brand_clearspace_body()}</p>

          <h3>{m.brand_misuse_title()}</h3>
          {/*
            Not `PlainList`: its marker is a check, and a check beside "do not
            stretch the lockup" says the opposite of the sentence it sits on.
          */}
          <ul className="dont-list">
            {[
              m.brand_misuse_stretch(),
              m.brand_misuse_recolour(),
              m.brand_misuse_revive(),
              m.brand_misuse_ground(),
              m.brand_misuse_retype(),
            ].map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="brand-section" id="colour">
          <h2>{m.brand_colour_title()}</h2>
          <p>{m.brand_colour_body()}</p>

          {palette.map((group) => (
            <div className="swatch-group" key={group.id}>
              <h3>{group.title}</h3>
              <p>{group.note}</p>
              <ul className="swatches">
                {group.swatches.map((swatch) => (
                  <li key={swatch.token}>
                    {/* The paint comes from the token, never from `hex`. */}
                    <span
                      className="swatch-chip"
                      style={{ background: `var(--${swatch.token})` }}
                    />
                    <span className="swatch-role">{swatch.role}</span>
                    <code>{swatch.hex}</code>
                    <span className="swatch-token">--{swatch.token}</span>
                    {swatch.contrast ? (
                      <span className="swatch-contrast">{swatch.contrast}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>

        <section className="brand-section" id="type">
          <h2>{m.brand_type_title()}</h2>
          <p>{m.brand_type_body()}</p>

          <ul className="scale">
            {scale.map((step) => (
              <li key={step.spec}>
                <p className="specimen" data-spec={step.spec}>
                  {m.brand_title()}
                </p>
                <p className="specimen-meta">
                  <span>{step.usage}</span> <code>{step.stated}</code>
                </p>
              </li>
            ))}
          </ul>

          <h3>{m.brand_eyebrow_title()}</h3>
          <p>{m.brand_eyebrow_body()}</p>
          <div className="eyebrow-compare">
            <div>
              <p className="page-label">{m.brand_type_section()}</p>
              <p className="small-note">{m.brand_eyebrow_use()}</p>
            </div>
            <div>
              <p className="eyebrow-avoid">{m.brand_type_section()}</p>
              <p className="small-note">{m.brand_eyebrow_avoid()}</p>
            </div>
          </div>

          <p className="small-note">{m.brand_type_locale_note()}</p>
        </section>

        <section className="brand-section" id="shape">
          <h2>{m.brand_shape_title()}</h2>
          <PlainList
            items={[
              m.brand_shape_radii(),
              m.brand_shape_depth(),
              m.brand_shape_motion(),
            ]}
          />
        </section>

        <section className="brand-section" id="access">
          <h2>{m.brand_access_title()}</h2>
          <p>{m.brand_access_body()}</p>
          <PlainList
            items={[
              m.brand_access_contrast(),
              m.brand_access_focus(),
              m.brand_access_meaning(),
            ]}
          />
        </section>
      </ReadLayout>

      <InnerCta lede={m.brand_cta_lede()} title={m.brand_cta_title()} />
    </>
  );
}
